import { getDirMode } from 'mobile_web/modules/platform'
import strings from 'mobile_web/strings'
import React, { useState } from 'react'

import { getDirectApi } from 'mytours-read-common'
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardSubtitle,
	CardTitle,
	Checkbox,
	Input,
	Note,
	Item,
	Label,
} from '@mytours/components'
import Modal from '../modal'
import './styles.less'
import { Form, FormField } from 'mytours-read-api-typescript'
import { DEFAULT_APP_INFO } from '../../../settings/functions'

interface Props {
	backdropDismiss?: boolean
	showBackdrop?: boolean
	isOpen: boolean
	onDidDismiss?: () => void
	form: Form
	skippable: boolean
	onFinish: () => void
}

const FormModal: React.FC<Props> = ({
	backdropDismiss = true,
	showBackdrop = true,
	isOpen,
	onDidDismiss,
	form,
	skippable,
	onFinish,
}): JSX.Element => {
	const [formInputs, setFormInputs] = useState<Record<string, string>>({})
	const [stqryRejectedInput, setStqryRejectedInput] = useState('')
	const [formErrors, setFormErrors] = useState<Record<string, string>>({})

	const updateFormInput = (field: FormField, value: string) => {
		setFormInputs({ ...formInputs, [field.slug]: String(value) })
		if (formErrors[field.slug]) {
			const newFormErrors = { ...formErrors }
			delete newFormErrors[field.slug]
			setFormErrors(newFormErrors)
		}
	}

	const validate = () => {
		const newFormErrors = { ...formErrors }
		form.fields.forEach(field => {
			if (!field.required) {
				return
			}

			if (field.kind === FormField.KindEnum.MarketingList) {
				if (formInputs[field.slug] !== 'true') {
					newFormErrors[field.slug] = skippable
						? strings.form_marketing_list_skippable_empty
						: strings.form_marketing_list_empty
				}
			} else if (!formInputs[field.slug]) {
				newFormErrors[field.slug] = strings.form_field_empty
			}
		})
		setFormErrors(newFormErrors)
		if (Object.keys(newFormErrors).length === 0) {
			return true
		} else {
			return false
		}
	}

	const submit = async () => {
		if (!validate()) {
			return
		}

		const formData = new FormData()
		formData.append('stqry_rejected', stqryRejectedInput)
		form.fields.forEach(field => {
			let fieldValue = formInputs[field.slug]
			if (!fieldValue && field.kind === FormField.KindEnum.MarketingList) {
				fieldValue = 'false'
			}
			formData.append(field.slug, fieldValue)
		})
		try {
			await getDirectApi().formSubmit(form.id, { body: formData })
			onFinish()
		} catch (error) {
			if (error instanceof Response) {
				const errorMessage = await error.json()
				const newFormErrors = { ...formErrors }
				errorMessage.errors.forEach(({ field: slug, message }) => {
					if (form.fields.some(field => field.slug === slug)) {
						newFormErrors[slug] = message
					}
				})
				setFormErrors(newFormErrors)
			}
		}
	}

	return (
		<Modal
			data-cy="form-modal"
			isOpen={isOpen}
			showBackdrop={showBackdrop}
			backdropDismiss={backdropDismiss}
			cssClass="form-select-modal"
			onDidDismiss={onDidDismiss}
		>
			<Card variant="ios" dir={getDirMode()}>
				<CardHeader>
					<div className="header">
						<CardTitle as="h2">{form.title}</CardTitle>
						{form.instructions ? (
							<CardSubtitle
								as="div"
								role="heading"
								aria-level={3}
								className="body-text"
								dangerouslySetInnerHTML={{ __html: `${form.instructions}` }}
							/>
						) : null}
					</div>
				</CardHeader>
				<CardContent>
					{/* To help detect spam: automated one might fill this field and get rejected by backend */}
					<div className="reject-field">
						<Item variant="ios" as="div">
							<Label variant="ios" as="span">
								{'Label'}
							</Label>
							{/* Ionic named the field from the item's label; the name stays now that nothing wires it. */}
							<Input
								variant="ios"
								aria-label="Label"
								value={stqryRejectedInput}
								onChange={e => setStqryRejectedInput(e.target.value)}
							/>
						</Item>
					</div>
					{form.fields.map(field => {
						const error = formErrors[field.slug]
						switch (field.kind) {
							case FormField.KindEnum.Email:
							case FormField.KindEnum.Text:
							case FormField.KindEnum.GivenName:
							case FormField.KindEnum.FamilyName:
								return (
									<React.Fragment key={field.id}>
										<Label variant="ios" as="span" id={`form-field-${field.id}-label`}>
											{field.label}
										</Label>
										<Item variant="ios" as="div" invalid={!!error}>
											<Input
												variant="ios"
												aria-labelledby={`form-field-${field.id}-label`}
												value={formInputs[field.slug] ?? ''}
												onChange={e => updateFormInput(field, e.target.value)}
											/>
											<Note variant="ios" slot="error">
												{error}
											</Note>
										</Item>
									</React.Fragment>
								)
							case FormField.KindEnum.MarketingList:
								return (
									<Item variant="ios" as="div" key={field.id} className="checkbox-field" activatable invalid={!!error}>
										<Checkbox
											id={`form-field-${field.id}`}
											className="checkbox-field-control ion-focusable"
											checkmark="sharp"
											aria-labelledby={`form-field-${field.id}-label`}
											checked={formInputs[field.slug] === 'true'}
											onCheckedChange={checked => updateFormInput(field, String(checked))}
										/>
										<Label variant="ios" as="span" wrap id={`form-field-${field.id}-label`}>
											{field.label}
										</Label>
										{/* Covers the item, as Ionic's hidden checkbox label did, so a click anywhere toggles. */}
										<label
											htmlFor={`form-field-${field.id}`}
											className="checkbox-field-cover"
											aria-labelledby={`form-field-${field.id}-label`}
											aria-hidden
										/>
										<Note variant="ios" slot="error">
											{error}
										</Note>
									</Item>
								)
							default:
								return null
						}
					})}

					<div className="buttons-container">
						{form.options?.enable_cancel_button ? (
							<Button
								variant="clear"
								onClick={() => {
									window.location.href = `https://stqry.app/projects/${DEFAULT_APP_INFO.projectId}`
								}}
								aria-label={form.cancel_button_text}
								className="btn clear"
							>
								{form.cancel_button_text || strings.cancel}
							</Button>
						) : skippable ? (
							<Button variant="clear" onClick={onFinish} aria-label={strings.form_skip} className="btn clear">
								{strings.form_skip}
							</Button>
						) : null}

						<Button
							variant="clear"
							onClick={submit}
							aria-label={form.submit_button_text || strings.submit}
							className="btn"
						>
							{form.submit_button_text || strings.submit}
						</Button>
					</div>
				</CardContent>
			</Card>
		</Modal>
	)
}

export default FormModal
